import { Box, Button, InputBase, Tooltip } from '@mui/material';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

interface InlineEditProps {
    value: string;
    onSubmit: (value: string) => void;
    onCancel: () => void;
    /**
     * Localized accessible name for the text box. The editor replaces its
     * labelled trigger on activation, so without this a screen reader announces
     * an unnamed textbox and the user loses track of what they are editing.
     * Pass an already-translated string (e.g. `intl.formatMessage`).
     *
     * Optional only so the call sites that predate it keep compiling; every new
     * call site should pass it.
     */
    ariaLabel?: string;
    /**
     * Open with the whole value selected, so typing replaces it. Off by
     * default: the editor opens with the caret at the end, since a name is
     * usually edited rather than rewritten. A short value that is normally
     * retyped, such as a number, opts in.
     */
    selectAllOnOpen?: boolean;
    /**
     * Keep the line breaks in the value, for a field whose DICOM VR allows
     * them and that a single-line box would silently flatten. Enter adds a
     * break, so the editor carries its own Save and Cancel, and
     * Ctrl/Cmd+Enter saves from the keyboard. Clicking away neither saves
     * nor discards: the editor stays up with the text and both buttons.
     */
    multiline?: boolean;
    validate?: (value: string) => string | null;
    fontSize?: number;
    fontWeight?: number;
    tooltipOffset?: [number, number];
}

export default function InlineEdit({
    value: initialValue,
    onSubmit,
    onCancel,
    ariaLabel,
    selectAllOnOpen = false,
    multiline = false,
    validate,
    fontSize = 14,
    fontWeight = 500,
    tooltipOffset = [-4, -8],
}: InlineEditProps) {
    const [value, setValue] = useState(initialValue);
    // A multiline editor renders a <textarea>; both carry focus,
    // select() and setSelectionRange.
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    // Blur-to-submit is armed a tick after mount, once the click / double-click
    // that opened the editor has fully resolved. A stray blur fired during that
    // resolution — the "immediate-blur race" that closed the editor before the
    // user could type — is caught in handleBlur and reclaims focus instead of
    // submitting.
    const blurArmedRef = useRef(false);
    const errorId = useId();

    const intl = useIntl();
    const error = useMemo(() => validate?.(value) ?? null, [value, validate]);
    const tooltipContent =
        error ||
        intl.formatMessage({
            id: multiline ? 'inlineEdit.hint.multiline' : 'inlineEdit.hint',
        });

    useEffect(() => {
        const input = inputRef.current;
        if (!input) {
            return;
        }
        input.focus();
        if (selectAllOnOpen) {
            input.select();
        } else {
            const end = input.value.length;
            input.setSelectionRange(end, end);
        }
    }, [selectAllOnOpen]);

    useEffect(() => {
        const armTimer = setTimeout(() => {
            blurArmedRef.current = true;
        }, 0);
        return () => clearTimeout(armTimer);
    }, []);

    // A pointer event inside the editor is editing, not a click on what the
    // editor replaced: the host must not select, toggle or open on it, and a
    // host mousedown guard must not block placing the caret or shift-click
    // extending a selection.
    const stopPointerEvent = (e: React.SyntheticEvent) => {
        e.stopPropagation();
    };

    // Trailing spaces are insignificant padding in every DICOM string VR.
    // A multiline value is VR ST, where a line break and a leading space are
    // both content, so only the padding at the end comes off. An entry
    // refused by validation stays open with the reason shown: closing would
    // discard the text without a word.
    const handleSubmit = () => {
        if (error) {
            return;
        }
        const trimmedValue = multiline
            ? value.replace(/[ \t]+$/, '')
            : value.trim();
        if (trimmedValue !== initialValue) {
            onSubmit(trimmedValue);
        }
        onCancel();
    };

    // The InputBase submits on blur, but a blur fired before the editor has
    // settled (while the opening click/double-click is still resolving) would
    // close it before the user typed anything. Until blur-to-submit is armed,
    // reclaim focus and stay open; afterwards a blur commits as normal.
    const handleBlur = () => {
        if (!blurArmedRef.current) {
            inputRef.current?.focus();
            return;
        }
        // A multiline editor has its own Save and Cancel, so clicking away
        // is neither: dismissing the surface it sits on must not write a
        // half-typed clinical value.
        if (multiline) {
            return;
        }
        // A single-line value the user typed and left invalid is not
        // written, and the editor closes as it would on Esc.
        if (error) {
            onCancel();
            return;
        }
        handleSubmit();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Only swallow keys this component actually handles. Blanket-stopping
        // every keydown breaks global shortcuts (e.g. Ctrl+H Safe Mode toggle)
        // when the user has focused this inline editor.
        if (e.key === 'Enter' && (!multiline || e.ctrlKey || e.metaKey)) {
            e.stopPropagation();
            // Focus may return to a button as the editor closes; the same
            // Enter must not then press it.
            e.preventDefault();
            handleSubmit();
        } else if (e.key === 'Escape') {
            e.stopPropagation();
            onCancel();
        }
    };

    return (
        <Tooltip
            open
            id={error ? errorId : undefined}
            placement="top-start"
            title={tooltipContent}
            PopperProps={{
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: tooltipOffset,
                        },
                    },
                ],
            }}
            componentsProps={{
                tooltip: {
                    sx: error
                        ? {
                              bgcolor: 'error.main',
                              '& .MuiTooltip-arrow': {
                                  color: 'error.main',
                              },
                          }
                        : {},
                },
            }}
        >
            <Box
                onMouseDown={stopPointerEvent}
                onClick={stopPointerEvent}
                onDoubleClick={stopPointerEvent}
                // On the wrapper, so Esc and Ctrl+Enter still work while
                // focus is on the multiline editor's buttons.
                onKeyDown={handleKeyDown}
            >
                <InputBase
                    inputRef={inputRef}
                    multiline={multiline}
                    minRows={multiline ? 3 : undefined}
                    // A textarea falls back to its intrinsic ~20 character
                    // width, which wraps a description into a column a few
                    // words wide. Single-line editors keep sizing to their
                    // content, as the panels around them expect.
                    fullWidth={multiline}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onBlur={handleBlur}
                    sx={{
                        fontSize,
                        fontWeight,
                        padding: 0,
                        ...(multiline && {
                            px: 1,
                            py: 0.75,
                            border: 1,
                            borderRadius: 1,
                            borderColor: error ? 'error.main' : 'primary.main',
                            bgcolor: 'background.sunken',
                        }),
                        '& input, & textarea': {
                            padding: 0,
                            color: error ? 'error.main' : 'inherit',
                        },
                    }}
                    // The test id and label are forwarded to the inner text box
                    // rather than the wrapper <div>, so Playwright's
                    // toBeFocused() and inputValue() target the actual focused
                    // element and the accessible name lands on the text box.
                    // `aria-invalid` + `aria-describedby` expose the validation
                    // state and message to assistive tech: describedby points at
                    // the tooltip, which carries the error text via the shared
                    // errorId, so the message lives in exactly one node — no
                    // duplicate that would trip a getByText locator or be read
                    // twice by a screen reader.
                    inputProps={{
                        'data-testid': 'inline-edit-input',
                        'aria-label': ariaLabel,
                        'aria-invalid': error ? true : undefined,
                        'aria-describedby': error ? errorId : undefined,
                    }}
                />
                {multiline && (
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={1}
                        mt={1}
                    >
                        <Button size="small" onClick={onCancel}>
                            <FormattedMessage id="cancel" />
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            disabled={!!error}
                            onClick={handleSubmit}
                            aria-describedby={error ? errorId : undefined}
                        >
                            <FormattedMessage id="save" />
                        </Button>
                    </Box>
                )}
            </Box>
        </Tooltip>
    );
}
